import React, { useEffect, useMemo, useState } from "react";
import {
  buildConflictAwareRoute,
  findFeasibleRouteOrder,
} from "./routeGeometry";

const ROUTE_CODES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const TrashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2 4 14 4" />
    <path d="M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1" />
    <path d="M12 4l-.8 9.2A1 1 0 0110.2 14H5.8a1 1 0 01-1-.8L4 4" />
    <line x1="7" y1="7" x2="7" y2="11" />
    <line x1="9" y1="7" x2="9" y2="11" />
  </svg>
);

const IcleanViewCleanRoutes = ({
  cleanRouteMode,
  setCleanRouteMode,
  hideCreate = false,
  rects = [],
  rootPpm = null,
  onRouteStepMapChange,
  onRouteModeStateChange,
  onCanvasLocationSelect,
  onCheckedRoutesChange,
  onOtherRouteLocationsChange,
  embedded = false,
}) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [checkedRouteIds, setCheckedRouteIds] = useState(new Set());
  const [editingName, setEditingName] = useState("");
  const [editingCode, setEditingCode] = useState("A");
  const [draggingLocationId, setDraggingLocationId] = useState(null);
  // dropIndicator: { afterLocationId: string | null } — null means "before first"
  const [dropIndicator, setDropIndicator] = useState(null);
  const [isAddingLocations, setIsAddingLocations] = useState(false);
  const [mopSpeed, setMopSpeed] = useState(200); // m²/hour, editable
  const [optimiseStatus, setOptimiseStatus] = useState(null); // null | {changed, penaltyBefore, penaltyAfter, otherSegCount}
  // Refs for reliable access in event handlers (avoid stale closure)
  const draggingIdRef = React.useRef(null);
  const dropIndicatorRef = React.useRef(null);
  const selectedRouteRef = React.useRef(null);
  // Track in-flight location append requests to prevent duplicate submissions
  const pendingAppendIds = React.useRef(new Set());

  const selectedRoute = routes.find((r) => r.id === selectedRouteId) || null;

  const routeStepMap = useMemo(() => {
    if (!selectedRoute?.steps) return {};
    return selectedRoute.steps.reduce((acc, step) => {
      acc[step.location_id] = step.step_order;
      return acc;
    }, {});
  }, [selectedRoute]);

  useEffect(() => {
    onRouteStepMapChange && onRouteStepMapChange(routeStepMap);
  }, [routeStepMap, onRouteStepMapChange]);

  useEffect(() => {
    const hasSelected = cleanRouteMode && !!selectedRouteId;
    onRouteModeStateChange && onRouteModeStateChange(hasSelected);
  }, [cleanRouteMode, selectedRouteId, onRouteModeStateChange]);

  const loadRoutes = async () => {
    if (!cleanRouteMode) return;
    setLoading(true);
    try {
      const response = await fetch("/api/clean-routes", { credentials: "include" });
      const data = await response.json();
      if (data.success) {
        const list = data.routes || [];
        setRoutes(list);
        setSelectedRouteId((prev) => {
          if (!prev) return null;
          return list.some((r) => r.id === prev) ? prev : null;
        });
      }
    } catch (err) {
      console.error("Clean routes load hiba:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanRouteMode]);

  useEffect(() => {
    if (!onCheckedRoutesChange) return;
    const checked = routes.filter((r) => checkedRouteIds.has(r.id));
    onCheckedRoutesChange(checked);
  }, [checkedRouteIds, routes, onCheckedRoutesChange]);

  // Compute location IDs that belong to OTHER routes (not the selected one).
  // Used by the canvas to show hatching and block clicks on already-covered locations.
  useEffect(() => {
    if (!onOtherRouteLocationsChange) return;
    if (!selectedRouteId) {
      onOtherRouteLocationsChange([]);
      return;
    }
    const ids = new Set();
    routes.forEach((r) => {
      if (r.id === selectedRouteId) return;
      (r.steps || []).forEach((s) => ids.add(String(s.location_id)));
    });
    onOtherRouteLocationsChange([...ids]);
  }, [routes, selectedRouteId, onOtherRouteLocationsChange]);

  useEffect(() => {
    setCheckedRouteIds((prev) => {
      const validIds = new Set(routes.map((r) => r.id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [routes]);

  useEffect(() => {
    if (!selectedRoute) {
      setEditingName("");
      setEditingCode("A");
      setIsAddingLocations(false);
      return;
    }
    setEditingName(selectedRoute.name || "");
    setEditingCode(selectedRoute.code || "A");
    const speed = selectedRoute.mop_speed_m2h || 200;
    setMopSpeed(speed);

    // Auto-compute and save duration_minutes when route is selected
    if (rootPpm && rects.length > 0) {
      const routeSteps = selectedRoute.steps || [];
      const area = routeSteps.reduce((sum, step) => {
        const loc = rects.find((r) => String(r.id) === String(step.location_id));
        return loc ? sum + (loc.width / rootPpm) * (loc.height / rootPpm) : sum;
      }, 0);
      const computed = speed > 0 && area > 0 ? Math.round((area / speed) * 60) : null;
      if (computed && computed !== selectedRoute.duration_minutes) {
        fetch(`/api/clean-routes/${selectedRoute.id}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ duration_minutes: computed }),
        }).then(() => {
          setRoutes((prev) =>
            prev.map((r) =>
              r.id === selectedRoute.id ? { ...r, duration_minutes: computed } : r
            )
          );
        }).catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRouteId]);

  useEffect(() => {
    setOptimiseStatus(null);
  }, [selectedRouteId]);

  const usedCodes = new Set(routes.map((r) => r.code));
  const availableCodes = ROUTE_CODES.filter((c) => !usedCodes.has(c));
  const nextCode = availableCodes[0] || "A";

  // Create a new route immediately with a default name and open it for editing
  const handleCreateRoute = async () => {
    const code = nextCode;
    const name = `Route ${code}`;
    try {
      const response = await fetch("/api/clean-routes", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, name }),
      });
      const data = await response.json();
      if (!response.ok) {
        console.error("Create route:", data.detail || response.status);
        return;
      }
      if (data.success) {
        await loadRoutes();
        setSelectedRouteId(data.route.id);
        setIsAddingLocations(true);
      }
    } catch (err) {
      console.error("Create route hiba:", err);
    }
  };

  // Auto-save on blur / select change
  const handleAutoSave = async ({ name, code }) => {
    if (!selectedRoute) return;
    const nextName = (name ?? editingName).trim();
    const nextCode = (code ?? editingCode).trim();
    try {
      const response = await fetch(`/api/clean-routes/${selectedRoute.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: nextName, code: nextCode }),
      });
      const data = await response.json();
      if (data.success) {
        setRoutes((prev) => prev.map((r) => (r.id === data.route.id ? data.route : r)));
      }
    } catch (err) {
      console.error("Auto-save route hiba:", err);
    }
  };

  const handleDeleteRoute = async () => {
    if (!selectedRoute) return;
    if (!window.confirm("Are you sure you want to delete this route?")) return;
    try {
      const response = await fetch(`/api/clean-routes/${selectedRoute.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        console.error("Delete route:", data.detail || response.status);
        return;
      }
      if (data.success) {
        setSelectedRouteId(null);
        await loadRoutes();
      }
    } catch (err) {
      console.error("Delete route hiba:", err);
    }
  };

  const handleAppendLocation = async (locationId) => {
    if (!cleanRouteMode || !selectedRoute) return;
    // Deduplicate: ignore if a request for this location is already in flight
    if (pendingAppendIds.current.has(locationId)) return;
    pendingAppendIds.current.add(locationId);
    try {
      const response = await fetch(`/api/clean-routes/${selectedRoute.id}/steps`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location_id: locationId }),
      });
      const data = await response.json();
      if (data.success) {
        setRoutes((prev) =>
          prev.map((r) => (r.id === selectedRoute.id ? { ...r, steps: data.steps || [] } : r))
        );
      } else {
        console.error("Append route step:", data.detail || "unknown error");
      }
    } catch (err) {
      console.error("Append route step hiba:", err);
    } finally {
      pendingAppendIds.current.delete(locationId);
    }
  };

  const handleRemoveStep = async (locationId) => {
    if (!selectedRoute) return;
    try {
      const response = await fetch(
        `/api/clean-routes/${selectedRoute.id}/steps/${locationId}`,
        { method: "DELETE", credentials: "include" }
      );
      const data = await response.json();
      if (!response.ok) {
        console.error("Remove route step:", data.detail || response.status);
        return;
      }
      if (data.success) {
        setRoutes((prev) =>
          prev.map((r) => (r.id === selectedRoute.id ? { ...r, steps: data.steps || [] } : r))
        );
      }
    } catch (err) {
      console.error("Remove route step hiba:", err);
    }
  };

  const handleReorderSteps = async (orderedLocationIds) => {
    const route = selectedRouteRef.current;
    if (!route) return false;
    // shadow for use below
    const selectedRoute = route;
    try {
      const response = await fetch(
        `/api/clean-routes/${selectedRoute.id}/steps/reorder`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ location_ids: orderedLocationIds }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        console.error("Reorder route steps:", data.detail || response.status, data);
        return false;
      }
      if (data.success) {
        setRoutes((prev) =>
          prev.map((r) => (r.id === selectedRoute.id ? { ...r, steps: data.steps || [] } : r))
        );
        return true;
      }
      return false;
    } catch (err) {
      console.error("Reorder route steps hiba:", err);
      return false;
    }
  };

  const handleOptimiseRoute = async () => {
    const route = selectedRouteRef.current;
    if (!route) return;
    const steps = route.steps || [];
    if (steps.length < 3) {
      setOptimiseStatus({
        running: false,
        changed: false,
        noFeasibleOrder: false,
        penaltyCount: 0,
        otherSegCount: 0,
        stepCount: steps.length,
        lastRunAt: Date.now(),
      });
      return;
    }
    setOptimiseStatus({ running: true });
    // Allow React to paint "running" state before heavy CPU work starts.
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const MARKER_MARGIN = 20;
      const rectById = new Map(rects.map((r) => [String(r.id), r]));

      const locationIds = steps
        .map((step) => step.location_id)
        .filter((id) => rectById.has(String(id)));
      if (locationIds.length < 3) {
        setOptimiseStatus({
          running: false,
          changed: false,
          noFeasibleOrder: false,
          penaltyCount: 0,
          otherSegCount: 0,
          stepCount: steps.length,
          lastRunAt: Date.now(),
        });
        return;
      }

      const otherRouteRects = routes
        .filter((r) => r.id !== route.id)
        .flatMap((r) => (r.steps || []).map((s) => rectById.get(String(s.location_id))).filter(Boolean));

      const otherRouteSegments = routes
        .filter((r) => r.id !== route.id)
        .flatMap((r) => {
          const ids = (r.steps || [])
            .slice()
            .sort((a, b) => a.step_order - b.step_order)
            .map((s) => s.location_id)
            .filter((id) => rectById.has(String(id)));
          const built = buildConflictAwareRoute(ids, {
            rectById,
            margin: MARKER_MARGIN,
            otherRouteSegments: [],
            otherRouteRects: [],
            allowCorner: true,
          });
          return built.segments;
        });

      const geometryCtx = {
        rectById,
        margin: MARKER_MARGIN,
        otherRouteSegments,
        otherRouteRects,
        allowCorner: true,
      };

      await new Promise((resolve) => setTimeout(resolve, 0));
      const searchResult = findFeasibleRouteOrder(locationIds, geometryCtx, {
        exploreLimit: 500000,
      });

      const bestOrder = searchResult.order;
      const bestScore = searchResult.built?.score || {
        feasible: false,
        penaltyCount: 0,
        selfConflicts: 0,
        otherConflicts: 0,
        branchConflicts: 0,
        turns: 0,
      };

      if (!searchResult.built?.feasible) {
        setOptimiseStatus({
          running: false,
          changed: false,
          noFeasibleOrder: true,
          currentRouteFeasible: false,
          penaltyCount: bestScore.penaltyCount,
          selfConflictCount: bestScore.selfConflicts,
          otherConflictCount: bestScore.otherConflicts,
          branchConflictCount: bestScore.branchConflicts,
          turnCount: bestScore.turns,
          otherSegCount: otherRouteSegments.length,
          stepCount: steps.length,
          lastRunAt: Date.now(),
        });
        if (typeof window !== "undefined" && typeof window.alert === "function") {
          window.alert(
            `No intersection-free and branch-free order was found.\n` +
            `Self-intersections: ${bestScore.selfConflicts}, other route conflicts: ${bestScore.otherConflicts}, branches: ${bestScore.branchConflicts}.`
          );
        }
        return;
      }

      const finalIds = bestOrder;
      const initialIds = locationIds;
      const changed = finalIds.some((id, i) => String(id) !== String(initialIds[i]));

      setOptimiseStatus({
        running: false,
        changed,
        penaltyCount: bestScore.penaltyCount,
        noFeasibleOrder: false,
        currentRouteFeasible: true,
        selfConflictCount: bestScore.selfConflicts,
        otherConflictCount: bestScore.otherConflicts,
        branchConflictCount: bestScore.branchConflicts,
        turnCount: bestScore.turns,
        otherSegCount: otherRouteSegments.length,
        stepCount: steps.length,
        lastRunAt: Date.now(),
      });

      if (changed) {
        const ok = await handleReorderSteps(finalIds);
        if (!ok) {
          setOptimiseStatus({
            running: false,
            changed: false,
            error: true,
            message: "reorder request failed",
            lastRunAt: Date.now(),
          });
          if (typeof window !== "undefined" && typeof window.alert === "function") {
            window.alert("The optimised order was found, but an error occurred while saving it.");
          }
        }
      }
    } catch (err) {
      console.error("Optimise route hiba:", err);
      setOptimiseStatus({
        running: false,
        changed: false,
        noFeasibleOrder: false,
        error: true,
        message: String(err?.message || err || "unknown error"),
        lastRunAt: Date.now(),
      });
      if (typeof window !== "undefined" && typeof window.alert === "function") {
        window.alert("An error occurred during optimisation. Check the console for details.");
      }
    }
  };

  // Drop indicator: track which gap the dragged item will land in
  const handleStepDragOver = (e, locationId) => {
    e.preventDefault();
    const domRect = e.currentTarget.getBoundingClientRect();
    const midY = domRect.top + domRect.height / 2;
    const currentSteps = selectedRouteRef.current?.steps || [];
    const idx = currentSteps.findIndex((s) => s.location_id === locationId);
    const indicator = e.clientY < midY
      ? { afterLocationId: idx > 0 ? currentSteps[idx - 1].location_id : null }
      : { afterLocationId: locationId };
    dropIndicatorRef.current = indicator;
    setDropIndicator(indicator);
  };

  const executeDrop = async () => {
    const route = selectedRouteRef.current;
    const dragId = draggingIdRef.current;
    const indicator = dropIndicatorRef.current;
    if (!route || dragId === null || !indicator) return;

    const currentSteps = route.steps || [];
    const fromIndex = currentSteps.findIndex((s) => s.location_id === dragId);
    if (fromIndex === -1) return;

    const reordered = [...currentSteps];
    const [moved] = reordered.splice(fromIndex, 1);
    const afterId = indicator.afterLocationId;
    if (afterId === null) {
      reordered.unshift(moved);
    } else {
      const afterIdx = reordered.findIndex((s) => s.location_id === afterId);
      if (afterIdx === -1) return;
      reordered.splice(afterIdx + 1, 0, moved);
    }

    // Reset drag state immediately
    draggingIdRef.current = null;
    dropIndicatorRef.current = null;
    setDraggingLocationId(null);
    setDropIndicator(null);

    await handleReorderSteps(reordered.map((s) => s.location_id));
  };

  useEffect(() => {
    if (!onCanvasLocationSelect) return;
    if (!cleanRouteMode || !selectedRoute || !isAddingLocations) {
      onCanvasLocationSelect(null);
      return;
    }
    onCanvasLocationSelect(handleAppendLocation);
    return () => onCanvasLocationSelect(null);
  }, [cleanRouteMode, selectedRoute, isAddingLocations, onCanvasLocationSelect]);

  // Keep ref in sync so event handlers always see the current route
  selectedRouteRef.current = selectedRoute;

  if (!cleanRouteMode) return null;

  const steps = selectedRoute?.steps || [];

  return (
    <section className={`block w-full text-slate-100 text-xs ${embedded ? "p-3" : "border-b border-slate-800 p-4"}`}>
      {/* Header */}
      {!embedded && <div className="flex justify-between items-center mb-3">
        <div className="text-[0.7rem] text-slate-200 font-semibold uppercase tracking-wide">
          Clean route
        </div>
        <button
          onClick={() => setCleanRouteMode(false)}
          className="w-5 h-5 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-slate-100 text-sm"
          title="Close"
        >
          ×
        </button>
      </div>}

      {/* Route list */}
      <div className="space-y-2 mb-3">
        <button
          onClick={handleCreateRoute}
          className={`w-full px-2 py-1 rounded border text-[0.7rem] bg-slate-800 text-slate-100 border-slate-600 hover:bg-slate-700 ${hideCreate ? "hidden" : ""}`}
        >
          + Create new clean route
        </button>

        <div className="space-y-2 rounded-lg border border-slate-700 bg-slate-950/40 p-2">
          <div className="text-[0.7rem] font-semibold text-slate-300 px-1">
            Existing routes {loading ? "(loading...)" : `(${routes.length})`}
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {routes.map((route) => (
              <div
                key={route.id}
                onClick={() => setSelectedRouteId((prev) => (prev === route.id ? null : route.id))}
                className={`flex items-center justify-between px-2 py-1 rounded border text-[0.65rem] cursor-pointer ${
                  selectedRouteId === route.id
                    ? "bg-red-700/30 border-red-400 text-red-100"
                    : "bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
                }`}
              >
                <span>{route.code} - {route.name}</span>
                <input
                  type="checkbox"
                  checked={checkedRouteIds.has(route.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    setCheckedRouteIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(route.id)) next.delete(route.id);
                      else next.add(route.id);
                      return next;
                    });
                  }}
                  className="accent-teal-400 cursor-pointer w-3.5 h-3.5 shrink-0"
                  title="Show on canvas"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit panel */}
      {selectedRoute && (() => {
        // Stats computation
        const totalAreaM2 = rootPpm ? steps.reduce((sum, step) => {
          const loc = rects.find((r) => String(r.id) === String(step.location_id));
          if (!loc) return sum;
          return sum + (loc.width / rootPpm) * (loc.height / rootPpm);
        }, 0) : 0;
        const estMinutes = mopSpeed > 0 ? Math.round((totalAreaM2 / mopSpeed) * 60) : 0;
        const estH = Math.floor(estMinutes / 60);
        const estM = estMinutes % 60;
        const estTimeStr = estH > 0 ? `${estH}h ${estM}min` : `${estM}min`;
        const hasPpm = !!rootPpm;

        return (
        <div className="space-y-2 mb-1 p-2 border border-slate-700 rounded bg-slate-800/40">
          <div className="text-[0.6rem] text-slate-400 font-semibold uppercase tracking-wide">
            Clean route edit
          </div>

          {/* Code + name on one row */}
          <div className="flex gap-1.5">
            <select
              value={editingCode}
              onChange={(e) => { setEditingCode(e.target.value); handleAutoSave({ code: e.target.value }); }}
              className="w-10 shrink-0 text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-1 py-1 text-slate-100"
            >
              {ROUTE_CODES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
            <input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => handleAutoSave({})}
              className="flex-1 min-w-0 text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
              placeholder="Route name"
            />
          </div>

          {/* Stats panel */}
          <div className="grid grid-cols-3 gap-1 rounded border border-slate-700 bg-slate-900/50 px-2 py-1.5 text-center">
            <div>
              <div className="text-[0.6rem] text-slate-400">Locations</div>
              <div className="text-[0.75rem] font-semibold text-slate-100">{steps.length}</div>
            </div>
            <div>
              <div className="text-[0.6rem] text-slate-400">Total m²</div>
              <div className="text-[0.75rem] font-semibold text-slate-100">
                {hasPpm ? `${Math.round(totalAreaM2)} m²` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[0.6rem] text-slate-400">Est. time</div>
              <div className="text-[0.75rem] font-semibold text-slate-100">
                {hasPpm && estMinutes > 0 ? estTimeStr : "—"}
              </div>
            </div>
          </div>

          {/* Mop speed input */}
          <div className="flex items-center gap-1.5">
            <span className="text-[0.6rem] text-slate-400 whitespace-nowrap">Speed:</span>
            <input
              type="text"
              inputMode="numeric"
              value={mopSpeed}
              onChange={(e) => { const v = Number(e.target.value); if (!isNaN(v)) setMopSpeed(v); }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.target.blur();
                }
              }}
              onBlur={() => {
                const v = Math.max(1, mopSpeed);
                setMopSpeed(v);
                if (selectedRoute) {
                  const computed = v > 0 ? Math.round((totalAreaM2 / v) * 60) : null;
                  const body = { mop_speed_m2h: v };
                  if (computed) body.duration_minutes = computed;
                  fetch(`/api/clean-routes/${selectedRoute.id}`, {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                  }).then(() => {
                    if (computed) setRoutes(prev => prev.map(r =>
                      r.id === selectedRoute.id ? { ...r, duration_minutes: computed } : r
                    ));
                  }).catch(() => {});
                }
              }}
              className="w-16 text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-1.5 py-0.5 text-slate-100 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-[0.6rem] text-slate-400">m²/h</span>
          </div>

          {/* Steps list */}
          <div className="flex items-center justify-between mt-2 mb-1">
            <div className="text-[0.6rem] text-slate-400">
              Click locations on canvas to append. Drag to reorder.
            </div>
            {steps.length >= 3 && (
              <div className="flex items-center gap-1.5">
                {optimiseStatus && !optimiseStatus.running && (
                  <span className="text-[0.55rem] text-slate-400">
                    {optimiseStatus.error
                      ? "✕ optimisation error"
                      : optimiseStatus.noFeasibleOrder
                      ? "✕ no valid order"
                      : optimiseStatus.currentRouteFeasible === false
                        ? "✕ invalid route"
                      : optimiseStatus.changed
                        ? "✓ reordered"
                        : "✓ valid"}
                    {" · "}{optimiseStatus.otherSegCount} other segments
                    {optimiseStatus.penaltyCount > 0 && (
                      <span className="text-orange-400"> · {optimiseStatus.penaltyCount} conflicts remaining</span>
                    )}
                    {optimiseStatus.branchConflictCount > 0 && (
                      <span className="text-orange-400"> · {optimiseStatus.branchConflictCount} branches</span>
                    )}
                    {optimiseStatus.lastRunAt && (
                      <span> · checked</span>
                    )}
                  </span>
                )}
                <button
                  onClick={handleOptimiseRoute}
                  disabled={optimiseStatus?.running}
                  className="px-2 py-0.5 rounded border text-[0.6rem] bg-teal-900/40 text-teal-300 border-teal-700 hover:bg-teal-800/60 shrink-0 disabled:opacity-50"
                  title="Reorder steps to minimise total route distance"
                >
                  {optimiseStatus?.running ? "⏳ Running..." : "✦ Optimise"}
                </button>
              </div>
            )}
          </div>

          <div
            className="space-y-0 max-h-56 overflow-y-auto pr-1"
            onDragOver={(e) => e.preventDefault()}
          >
            {/* Drop indicator before first item */}
            <div className={`h-0.5 rounded transition-all mb-0.5 ${dropIndicator?.afterLocationId === null ? "bg-teal-400" : "bg-transparent"}`} />

            {steps.map((step) => {
              const loc = rects.find((r) => r.id === step.location_id);
              const isDragging = draggingLocationId === step.location_id;
              return (
                <React.Fragment key={`${step.location_id}-${step.step_order}`}>
                  <div
                    draggable
                    onDragStart={() => {
                      draggingIdRef.current = step.location_id;
                      setDraggingLocationId(step.location_id);
                    }}
                    onDragOver={(e) => handleStepDragOver(e, step.location_id)}
                    onDrop={(e) => { e.preventDefault(); executeDrop(); }}
                    onDragEnd={() => {
                      draggingIdRef.current = null;
                      dropIndicatorRef.current = null;
                      setDraggingLocationId(null);
                      setDropIndicator(null);
                    }}
                    className={`flex items-center justify-between gap-2 rounded border bg-slate-900/60 px-2 py-1 cursor-grab active:cursor-grabbing mb-0.5 transition-opacity ${
                      isDragging ? "opacity-40 border-slate-600" : "border-slate-700"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      {/* Drag handle dots */}
                      <svg width="8" height="12" viewBox="0 0 8 12" fill="#64748b" className="shrink-0">
                        <circle cx="2" cy="2" r="1.2"/><circle cx="6" cy="2" r="1.2"/>
                        <circle cx="2" cy="6" r="1.2"/><circle cx="6" cy="6" r="1.2"/>
                        <circle cx="2" cy="10" r="1.2"/><circle cx="6" cy="10" r="1.2"/>
                      </svg>
                      <span className="text-teal-300 text-[0.65rem] shrink-0">{step.step_order}.</span>
                      <span className="text-slate-100 text-[0.65rem] truncate">
                        {loc?.name || `Location ${step.location_id}`}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveStep(step.location_id)}
                      className="text-slate-500 hover:text-red-300 shrink-0"
                      title="Remove"
                    >
                      <TrashIcon />
                    </button>
                  </div>

                  {/* Drop indicator line after this item */}
                  <div className={`h-0.5 rounded transition-all mb-0.5 ${dropIndicator?.afterLocationId === step.location_id ? "bg-teal-400" : "bg-transparent"}`} />
                </React.Fragment>
              );
            })}

            {steps.length === 0 && (
              <div className="text-[0.6rem] text-slate-500 py-1">No locations yet.</div>
            )}

            {/* Add location toggle — styled as a list item */}
            <button
              onClick={() => setIsAddingLocations((v) => !v)}
              className={`w-full flex items-center gap-2 rounded border px-2 py-1 mt-1 text-[0.65rem] cursor-pointer transition-colors ${
                isAddingLocations
                  ? "bg-teal-700/30 border-teal-400 text-teal-200"
                  : "bg-slate-900/40 border-slate-600 border-dashed text-slate-500 hover:border-teal-600 hover:text-teal-400"
              }`}
            >
              {isAddingLocations ? (
                <>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                    <circle cx="5" cy="5" r="4" fillOpacity="0.3" stroke="currentColor" strokeWidth="1.2"/>
                    <circle cx="5" cy="5" r="2"/>
                  </svg>
                  <span>Add location <span className="opacity-60">(click on canvas)</span></span>
                </>
              ) : (
                <>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <circle cx="5" cy="5" r="4"/>
                    <line x1="5" y1="2.5" x2="5" y2="7.5"/>
                    <line x1="2.5" y1="5" x2="7.5" y2="5"/>
                  </svg>
                  <span>Add location</span>
                </>
              )}
            </button>
          </div>

          {/* Delete at the very bottom */}
          <button
            onClick={handleDeleteRoute}
            className="w-full mt-3 px-2 py-1 rounded border text-[0.7rem] bg-red-700/20 text-red-300 border-red-500 hover:bg-red-700/30"
          >
            Delete clean route
          </button>
        </div>
        );
      })()}
    </section>
  );
};

export default IcleanViewCleanRoutes;
